import axios from 'axios';

// 🟢 Standardized Central API Configuration
// Resolves live backend URL in production automatically, with localhost fallback
export const getApiBaseUrl = () => {
  // 1. If explicit environment variable is set and not localhost
  if (process.env.REACT_APP_API_URL && !process.env.REACT_APP_API_URL.includes('localhost')) {
    return process.env.REACT_APP_API_URL.replace(/\/+$/, '');
  }

  // 2. Browser runtime: detect if running locally or over LAN (localhost, 127.0.0.1, or 192.168.x.x, 10.x.x.x, etc.)
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const isLocalOrLan =
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '10.0.2.2' ||
      hostname.endsWith('.local') ||
      /^192\.168\.\d{1,3}\.\d{1,3}$/.test(hostname) ||
      /^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname) ||
      /^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/.test(hostname);

    if (isLocalOrLan) {
      // Connect to the backend API running on the host machine at port 8081
      return `http://${hostname}:8081`;
    }

    // 3. True production domains (e.g. *.stgdev.tech, *.vercel.app) use same-origin reverse proxy
    return window.location.origin;
  }

  // 4. Fallback for local development
  return process.env.REACT_APP_API_URL || 'http://localhost:8081';
};

export const API_BASE_URL = getApiBaseUrl();

// Helper to get authenticated JWT token, rejecting invalid/undefined/prototype strings
export const getValidToken = () => {
  if (typeof window === 'undefined') return null;
  try {
    const token = localStorage.getItem('token');
    if (!token || token === 'undefined' || token === 'null' || token.startsWith('prototype-jwt-token')) {
      return null;
    }
    return token;
  } catch {
    return null;
  }
};

// Helper to check if current user is on unauthenticated screen
const isAuthScreen = () => {
  if (typeof window === 'undefined') return false;
  const token = getValidToken();
  if (token) {
    return false; // User has valid token
  }
  const path = window.location.pathname.toLowerCase();
  return (
    path === '/login' ||
    path === '/signup' ||
    path === '/forgot-password' ||
    path === '/reset-password'
  );
};

axios.defaults.baseURL = API_BASE_URL;

// 🟢 Patch axios.create so all custom instances (e.g. AssetContext, WorkforceContext) automatically inherit the production API URL
const originalAxiosCreate = axios.create.bind(axios);
axios.create = function (defaultConfig = {}) {
  if (defaultConfig.baseURL && defaultConfig.baseURL.includes('localhost:8081')) {
    defaultConfig.baseURL = defaultConfig.baseURL.replace(/https?:\/\/localhost:8081/, API_BASE_URL);
  }
  const instance = originalAxiosCreate(defaultConfig);
  instance.interceptors.request.use((config) => {
    if (config.baseURL && config.baseURL.includes('localhost:8081')) {
      config.baseURL = config.baseURL.replace(/https?:\/\/localhost:8081/, API_BASE_URL);
    }
    if (config.url && config.url.includes('localhost:8081')) {
      config.url = config.url.replace(/https?:\/\/localhost:8081/, API_BASE_URL);
    }
    if (config.url && (config.url.startsWith('/api/') || config.url === '/api') && !config.baseURL) {
      config.url = `${API_BASE_URL}${config.url}`;
    }
    const token = getValidToken();
    if (token && config.headers && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
  return instance;
};

// 🟢 Helper to check if a URL is an allowed authentication endpoint on the login screen
function isAllowedAuthUrl(rawUrl) {
  if (!rawUrl) return true;
  const url = (typeof rawUrl === 'string' ? rawUrl : rawUrl.url || '').toLowerCase();

  // Always allow external third-party requests (Google, Apple, OAuth providers, CDNs)
  if (
    url.startsWith('http') &&
    !url.includes('localhost:8081') &&
    !url.includes('stgdev.tech') &&
    !url.includes('briqerp')
  ) {
    return true;
  }

  // Allow login, signup, auth, forgot-password, health check, and profile endpoints
  return (
    url.includes('/auth') ||
    url.includes('/login') ||
    url.includes('/signup') ||
    url.includes('/register') ||
    url.includes('/forgot-password') ||
    url.includes('/verify-reset-code') ||
    url.includes('/reset-password') ||
    url.includes('/health') ||
    url.includes('/userinfo') ||
    url.includes('oauth2')
  );
}

// 🟢 GLOBAL AXIOS INTERCEPTOR:
// 1. Blocks unnecessary background requests while on login/signup page
// 2. Rewrites any localhost:8081 call to the live API_BASE_URL
// 3. Injects Bearer token automatically
axios.interceptors.request.use((config) => {
  // If on login/signup screen, silence background provider requests
  if (isAuthScreen()) {
    if (!isAllowedAuthUrl(config.url)) {
      return Promise.reject(new axios.Cancel('Background request blocked: user not logged in yet.'));
    }
  }

  if (config.baseURL && config.baseURL.includes('localhost:8081')) {
    config.baseURL = config.baseURL.replace(/https?:\/\/localhost:8081/, API_BASE_URL);
  }

  if (config.url && config.url.includes('localhost:8081')) {
    config.url = config.url.replace(/https?:\/\/localhost:8081/, API_BASE_URL);
  }

  if (config.url && (config.url.startsWith('/api/') || config.url === '/api') && !config.baseURL) {
    config.url = `${API_BASE_URL}${config.url}`;
  }

  const token = getValidToken();
  if (token && config.headers && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// 🟢 GLOBAL FETCH INTERCEPTOR:
if (typeof window !== 'undefined' && window.fetch) {
  const originalFetch = window.fetch;
  window.fetch = function (resource, init = {}) {
    let url = typeof resource === 'string' ? resource : resource?.url || '';

    // Silence background fetch requests while on login screen without throwing red toast errors
    if (isAuthScreen()) {
      if (!isAllowedAuthUrl(url)) {
        return Promise.resolve(new Response(JSON.stringify({ success: false, data: [] }), {
          status: 200,
          headers: { 'Content-Type': 'application/json' }
        }));
      }
    }

    if (url.includes('localhost:8081')) {
      url = url.replace(/https?:\/\/localhost:8081/, API_BASE_URL);
    } else if (url.startsWith('/api/') || url === '/api') {
      url = `${API_BASE_URL}${url}`;
    }

    const token = localStorage.getItem('token');
    if (token && !token.startsWith('prototype-jwt-token')) {
      init = init || {};
      init.headers = init.headers || {};
      if (init.headers instanceof Headers) {
        if (!init.headers.has('Authorization')) {
          init.headers.set('Authorization', `Bearer ${token}`);
        }
      } else if (!init.headers['Authorization'] && !init.headers['authorization']) {
        init.headers['Authorization'] = `Bearer ${token}`;
      }
    }
    return originalFetch.call(this, url, init);
  };
}

// Dedicated API instance
const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// REQUEST INTERCEPTOR for api instance
api.interceptors.request.use(
  (config) => {
    // If on login/signup screen, silence background provider requests
    if (isAuthScreen()) {
      if (!isAllowedAuthUrl(config.url)) {
        return Promise.reject(new axios.Cancel('Background request blocked: user not logged in yet.'));
      }
    }

    // Automatically strip duplicate /api prefix when calling api instance
    if (config.url && config.url.startsWith('/api/')) {
      config.url = config.url.replace(/^\/api/, '');
    }

    const token = getValidToken();
    if (token && config.headers && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// RESPONSE INTERCEPTOR
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    if (error.response?.status === 402) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('subscription-expired', { 
          detail: error.response?.data || { message: 'Subscription or trial expired' } 
        }));
      }
    }

    if (error.response?.status === 401) {
      const currentPath = typeof window !== 'undefined' ? window.location.pathname.toLowerCase() : '';
      if (
        currentPath !== '/login' &&
        currentPath !== '/signup' &&
        currentPath !== '/forgot-password' &&
        currentPath !== '/reset-password'
      ) {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('auth-unauthorized'));
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;